import React from 'react';
import { Bot, GitBranch, Cpu, Shuffle } from 'lucide-react';

// ==========================================
// Types
// ==========================================

interface PaletteItem {
  kind: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  executionMode: 'sequential' | 'parallel' | 'conditional' | 'race' | null;
}

export interface NodePaletteProps {
  /** Called on click-to-add; receives the step kind + its default executionMode */
  onAddStep: (kind: string, executionMode: 'sequential' | 'parallel' | 'conditional' | 'race' | null) => void;
}

// ==========================================
// Palette items
// ==========================================

const PALETTE_ITEMS: PaletteItem[] = [
  {
    kind: 'agent',
    label: 'Agent Step',
    description: 'An AI agent execution step',
    icon: <Bot size={14} />,
    executionMode: 'sequential',
  },
  {
    kind: 'sub-workflow',
    label: 'Sub-workflow',
    description: 'Embed a nested workflow',
    icon: <GitBranch size={14} />,
    executionMode: 'sequential',
  },
  {
    kind: 'processor',
    label: 'Processor',
    description: 'A deterministic data transform',
    icon: <Cpu size={14} />,
    executionMode: 'sequential',
  },
  {
    kind: 'fan-out',
    label: 'Fan-out',
    description: 'Parallel execution branches',
    icon: <Shuffle size={14} />,
    executionMode: 'parallel',
  },
];

// ==========================================
// Component
// ==========================================

/**
 * Node palette — sits left of the canvas in WorkflowBuilder (classic mode).
 * Items are both drag-to-canvas (HTML5 drag, application/node-type) and click-to-add.
 */
export const NodePalette: React.FC<NodePaletteProps> = ({ onAddStep }) => (
  <div className="w-36 shrink-0 bg-surface border rounded-lg p-2 flex flex-col gap-0.5 overflow-y-auto">
    <div className="text-[9px] font-semibold uppercase tracking-widest text-faint px-1 pb-1">Add Node</div>

    {PALETTE_ITEMS.map((item) => (
      <div
        key={item.kind}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('application/node-type', item.kind);
          e.dataTransfer.effectAllowed = 'copy';
        }}
        onClick={() => onAddStep(item.kind, item.executionMode)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onAddStep(item.kind, item.executionMode);
        }}
        title={item.description}
        className="flex items-center gap-2 px-2 py-2 rounded-sm cursor-grab hover:bg-surface-hover active:cursor-grabbing select-none border border-transparent hover:border transition-all"
      >
        <span className="text-faint shrink-0">{item.icon}</span>
        <div className="min-w-0">
          <div className="text-xs font-medium text-body truncate">{item.label}</div>
        </div>
      </div>
    ))}
  </div>
);
